import { PlusOutlined } from '@ant-design/icons';
import AppButton from '@nxsys/components/AppButton';
import AppCard from '@nxsys/components/AppCard';
import AppInputSearch from '@nxsys/components/AppInputSearch';
import AppsContent from '@nxsys/components/AppsContainer/AppsContent';
import AppsHeader from '@nxsys/components/AppsContainer/AppsHeader';
import AppTableContainer from '@nxsys/components/AppTableContainer';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppDispatch, AppState } from '@nxsys/store';
import { agencyAction } from '@nxsys/store/slices/agency/reducer/agencySlice';
import { GetAgencyContactsListParams } from '@nxsys/store/slices/agency/types';
import { Icons } from '@nxsys/types/icons';
import { Tooltip } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { debounce } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AgencyContactType } from '@nxsys/types/models/permissions';
import { formatStatus } from '@nxsys/utils';
import { showMessage } from '@nxsys/utils/message';
import AppTagStatus from '@nxsys/components/AppTagStatus';
import { AppConfirmationDelete } from '@nxsys/components/AppConfirmationDeleteModal';
import { useDeleteAgencyContactById } from '@nxsys/hooks/api/agency/useAgencyApi';
import AddAgencyContactModal from '../create/AddNewAgencyContactModal';
import DetailAgencyContactModal from '../detail/DetailAgencyContactModal';

const AgencyContact = ({
  data,
  hasBorder = true,
}: {
  data: IAgency;
  hasBorder?: boolean;
}) => {
  const { messages } = useTypedIntl();

  const dispatch = useDispatch<AppDispatch>();
  const { loading, list, pagination, queryParams } = useSelector(
    (state: AppState) => state.agencyStore.agencyContactList,
  );

  const [searchValue, setSearchValue] = useState('');
  const [isOpenAddNewContact, setIsOpenAddNewContact] = useState(false);
  const toggleAddNewContact = useCallback(() => {
    setIsOpenAddNewContact(!isOpenAddNewContact);
  }, [isOpenAddNewContact]);
  const [openDetailContactModal, setOpenDetailContactModal] = useState<{
    data: AgencyContactType | null;
    open: boolean;
  }>({
    data: null,
    open: false,
  });
  const toggleDetailContact = useCallback(() => {
    setOpenDetailContactModal({
      ...openDetailContactModal,
      open: !openDetailContactModal.open,
    });
  }, [openDetailContactModal.open]);

  useEffect(() => {
    handleFetchData({
      page: 0,
      size: 20,
    });
  }, []);

  const handleFetchData = (params: GetAgencyContactsListParams) => {
    const newParams = {
      ...params,
    };
    dispatch(
      agencyAction.getAgencyContactsById({
        id: data.id,
        params: newParams,
      }),
    );
  };

  const refetchData = useCallback(() => {
    handleFetchData({ ...queryParams });
  }, [queryParams]);

  const debouncedFetchData = useCallback(
    debounce((search: string) => {
      handleFetchData({
        ...queryParams,
        page: 0,
        size: 20,
        search: search,
      });
    }, 500),
    [queryParams],
  );

  const onChangePagination = (page: number, pageSize: number) => {
    const isPageSizeChanged = pageSize !== pagination.pageSize;
    const shouldResetPage = isPageSizeChanged && pagination.current > 0;
    handleFetchData({
      ...queryParams,
      page: shouldResetPage ? 0 : page - 1,
      size: pageSize,
    });
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchValue(value);
    debouncedFetchData(value);
  };

  const handleViewDetail = useCallback((record: AgencyContactType) => {
    handleOpenDetailContact(record);
  }, []);

  const handleAddNew = useCallback(() => {
    setIsOpenAddNewContact(true);
  }, []);

  const { run: deleteAgencyContactById } = useDeleteAgencyContactById();

  const handleConfirmDelete = (record: AgencyContactType) => {
    AppConfirmationDelete.open({
      title: messages['common.delete'],
      paragraph: messages['common.deleteConfirm'],
      onConfirm: async () => {
        await deleteAgencyContactById(record.id);
        showMessage({
          type: 'success',
          content: messages['common.deleteSuccess'] as string,
        });
        refetchData();
      },
    });
  };

  const columns: ColumnsType<AgencyContactType> = [
    {
      key: 'action',
      title: 'Action',
      align: 'left',
      dataIndex: '',
      width: 100,
      render: (record) => (
        <ul className="flex items-center justify-start gap-x-4">
          <li onClick={() => handleViewDetail(record)}>
            <Tooltip title={messages['common.view']}>
              <span>
                <Icons.IconEdit />
              </span>
            </Tooltip>
          </li>
          <li onClick={() => handleConfirmDelete(record)}>
            <Tooltip title={messages['common.delete']}>
              <span>
                <Icons.IconDelete />
              </span>
            </Tooltip>
          </li>
        </ul>
      ),
    },
    {
      key: 'status',
      title: messages['common.status'],
      align: 'center',
      render: (record) => {
        const status = record?.status;
        return (
          <span className={`${formatStatus(status)}`}>
            <AppTagStatus className={`tag-popup ${formatStatus(status)}`}>
              {formatStatus(status)}
            </AppTagStatus>
          </span>
        );
      },
      width: 150,
    },
    {
      key: 'id',
      title: 'ID',
      dataIndex: 'id',
      width: 150,
    },
    {
      key: 'name',
      title: messages['common.name'],
      dataIndex: 'name',
    },
    {
      key: 'companyRef',
      title: messages['common.payrollCompany'],
      dataIndex: 'companyRef',
      render: (value) => value?.companyName || '_',
    },
    {
      key: 'email',
      title: messages['common.email'],
      dataIndex: 'email',
    },
    {
      key: 'phone',
      title: messages['common.phone'],
      dataIndex: 'phone',
    },
    {
      key: 'department',
      title: messages['common.branch'],
      dataIndex: 'department',
    },
  ];

  const handleOpenDetailContact = useCallback((data: AgencyContactType) => {
    setOpenDetailContactModal({
      data,
      open: true,
    });
  }, []);

  return (
    <AppCard
      title={hasBorder ? messages['common.agencyContacts'] : undefined}
      hasBorder={hasBorder}
    >
      <AppsHeader>
        <div className="flex justify-between gap-x-2 w-full">
          <AppInputSearch value={searchValue} onChange={handleSearchChange} />
          <AppButton
            type="primary"
            name={messages['common.addNew']}
            leftIcon={<PlusOutlined />}
            onClick={handleAddNew}
          />
        </div>
      </AppsHeader>
      <AppsContent>
        <AppTableContainer
          data={list ?? []}
          loading={loading}
          page={pagination.current ?? 0}
          total={pagination.totalDocs ?? 0}
          pageSize={pagination.pageSize ?? 20}
          scroll={{ x: 1500, y: 700 }}
          titleEmpty={messages['agency.contact.empty']}
          columns={columns}
          onChangePagination={onChangePagination}
          noDataType={!!searchValue ? 'noDataFound' : 'noDataCreated'}
          addNewBtnOnClick={handleAddNew}
          dataTypeName={messages['common.agencyContacts']}
        />
      </AppsContent>
      <AddAgencyContactModal
        isOpen={isOpenAddNewContact}
        toggle={toggleAddNewContact}
        agencyData={data}
        refetchData={refetchData}
      />
      {openDetailContactModal.open && (
        <DetailAgencyContactModal
          isOpen={openDetailContactModal.open}
          toggle={toggleDetailContact}
          agencyData={data}
          refetchData={refetchData}
          data={openDetailContactModal.data as any}
        />
      )}
    </AppCard>
  );
};

export default AgencyContact;
